import React, { useEffect, useMemo, useRef, useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { auth, firestore } from "../../../firebaseConfig";
import Modal from "../../../components/ui/Modal";

const MONTH_OPTIONS = [
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" },
];

const ACCOUNT_OPTIONS = ["cash", "bank", "mpesa"];
const PAGE_SIZE = 20;
const YEARS_BEFORE = 5;
const YEARS_AFTER = 2;
const DELETE_CONFIRMATION_PREFIX = "A1B2C3";
const DELETED_RECORDS_COLLECTION = "accountingCashbookDeletedRecords";
export const DEFAULT_RECORD_SCOPE = "admin:cashbook";

const buildYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: YEARS_BEFORE + YEARS_AFTER + 1 }, (_, index) => currentYear - YEARS_BEFORE + index);
};

const formatMoney = (value) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getInitialFormState = (type = "receipt") => ({
  date: new Date().toISOString().slice(0, 10),
  type,
  particulars: "",
  folio: "",
  amount: "",
  accountAffected: "cash",
  cash: "",
  bank: "",
  mpesa: "",
  discounts: "",
  file: null,
});

const revokeIfBlobUrl = (url) => {
  if (typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

const seedRecords = [
  {
    id: "r1",
    type: "receipt",
    date: new Date().toISOString().slice(0, 10),
    particulars: "Customer payment",
    folio: "RCPT-001",
    cash: 1200,
    bank: 0,
    mpesa: 0,
    discounts: 0,
    createdByEmail: "system",
    createdAt: Date.now(),
    lastEditedByEmail: null,
    lastEditedAt: null,
    supportingFileName: "",
    supportingFileUrl: "",
  },
  {
    id: "p1",
    type: "payment",
    date: new Date().toISOString().slice(0, 10),
    particulars: "Office supplies",
    folio: "PMT-001",
    cash: 300,
    bank: 0,
    mpesa: 0,
    discounts: 0,
    createdByEmail: "system",
    createdAt: Date.now(),
    lastEditedByEmail: null,
    lastEditedAt: null,
    supportingFileName: "",
    supportingFileUrl: "",
  },
];

export const resolveStorageKey = (recordScope = DEFAULT_RECORD_SCOPE) =>
  `accountingCashbookRecords:${recordScope}`;

const parseStoredRecords = (value) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const RecordsTable = ({
  title,
  rows,
  page,
  onPageChange,
  totals,
  onEdit,
  onDelete,
}) => {
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const visibleRows = rows.slice(start, start + PAGE_SIZE);

  return (
    <section className="bg-white border border-slate-100 rounded-xl shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Particulars</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Folio</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Cash</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Bank</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Mpesa</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Discounts</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-sm text-slate-500 text-center">
                  No records found for the selected filters.
                </td>
              </tr>
            )}
            {visibleRows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-sm text-slate-700">{row.date}</td>
                <td className="px-3 py-2 text-sm text-slate-700">
                  <div className="font-medium">{row.particulars || "—"}</div>
                  <div className="text-[11px] text-slate-500">
                    Created by: {row.createdByEmail || "unknown"}
                    {row.lastEditedByEmail ? ` • Edited by: ${row.lastEditedByEmail}` : ""}
                  </div>
                </td>
                <td className="px-3 py-2 text-sm text-slate-700">{row.folio || "—"}</td>
                <td className="px-3 py-2 text-sm text-slate-700 text-right">{formatMoney(row.cash)}</td>
                <td className="px-3 py-2 text-sm text-slate-700 text-right">{formatMoney(row.bank)}</td>
                <td className="px-3 py-2 text-sm text-slate-700 text-right">{formatMoney(row.mpesa)}</td>
                <td className="px-3 py-2 text-sm text-slate-700 text-right">{formatMoney(row.discounts)}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="px-2 py-1 border border-blue-200 text-blue-700 rounded hover:bg-blue-50 disabled:opacity-40"
                      onClick={() => row.supportingFileUrl && window.open(row.supportingFileUrl, "_blank", "noopener,noreferrer")}
                      disabled={!row.supportingFileUrl}
                    >
                      View File
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 border border-emerald-200 text-emerald-700 rounded hover:bg-emerald-50"
                      onClick={() => onEdit(row)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 border border-rose-200 text-rose-700 rounded hover:bg-rose-50"
                      onClick={() => onDelete(row)}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-100">
            <tr>
              <td className="px-3 py-2 text-xs font-semibold text-slate-700" colSpan={3}>Totals</td>
              <td className="px-3 py-2 text-xs font-semibold text-slate-700 text-right">{formatMoney(totals.cash)}</td>
              <td className="px-3 py-2 text-xs font-semibold text-slate-700 text-right">{formatMoney(totals.bank)}</td>
              <td className="px-3 py-2 text-xs font-semibold text-slate-700 text-right">{formatMoney(totals.mpesa)}</td>
              <td className="px-3 py-2 text-xs font-semibold text-slate-700 text-right">{formatMoney(totals.discounts)}</td>
              <td className="px-3 py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs">
        <span className="text-slate-500">Showing {visibleRows.length} of {rows.length} records</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-2 py-1 border rounded disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => onPageChange((prev) => Math.max(1, prev - 1))}
          >
            Prev
          </button>
          <span className="text-slate-600">Page {page} of {totalPages}</span>
          <button
            type="button"
            className="px-2 py-1 border rounded disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => onPageChange((prev) => Math.min(totalPages, prev + 1))}
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
};

const AccountingCashbookTab = ({ recordScope = DEFAULT_RECORD_SCOPE }) => {
  const today = new Date();
  const yearOptions = useMemo(() => buildYearOptions(), []);
  const storageKey = useMemo(() => resolveStorageKey(recordScope), [recordScope]);
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [records, setRecords] = useState(() => {
    if (typeof window === "undefined") return seedRecords;
    return parseStoredRecords(window.localStorage.getItem(storageKey)) || seedRecords;
  });
  const [receiptPage, setReceiptPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [deleteKeyword, setDeleteKeyword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [form, setForm] = useState(getInitialFormState());
  const recordsRef = useRef(records);

  const currentUserEmail = auth?.currentUser?.email || "unknown";

  useEffect(() => {
    recordsRef.current = records;
  }, [records]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedRecords = parseStoredRecords(window.localStorage.getItem(storageKey));
    setRecords(storedRecords || seedRecords);
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(records));
  }, [records, storageKey]);

  useEffect(() => {
    return () => {
      recordsRef.current.forEach((record) => revokeIfBlobUrl(record.supportingFileUrl));
    };
  }, []);

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const recordDate = new Date(record.date);
      if (Number.isNaN(recordDate.getTime())) return false;

      const matchesMonthYear =
        recordDate.getMonth() === Number(selectedMonth) &&
        recordDate.getFullYear() === Number(selectedYear);

      if (!matchesMonthYear) return false;
      if (fromDate && record.date < fromDate) return false;
      if (toDate && record.date > toDate) return false;
      return true;
    });
  }, [records, selectedMonth, selectedYear, fromDate, toDate]);

  const receipts = useMemo(
    () => filteredRecords.filter((record) => record.type === "receipt"),
    [filteredRecords]
  );
  const payments = useMemo(
    () => filteredRecords.filter((record) => record.type === "payment"),
    [filteredRecords]
  );

  const summarize = (rows) =>
    rows.reduce(
      (acc, row) => ({
        cash: acc.cash + Number(row.cash || 0),
        bank: acc.bank + Number(row.bank || 0),
        mpesa: acc.mpesa + Number(row.mpesa || 0),
        discounts: acc.discounts + Number(row.discounts || 0),
      }),
      { cash: 0, bank: 0, mpesa: 0, discounts: 0 }
    );

  const receiptTotals = useMemo(() => summarize(receipts), [receipts]);
  const paymentTotals = useMemo(() => summarize(payments), [payments]);

  const receiptGrandTotal = receiptTotals.cash + receiptTotals.bank + receiptTotals.mpesa - receiptTotals.discounts;
  const paymentGrandTotal = paymentTotals.cash + paymentTotals.bank + paymentTotals.mpesa - paymentTotals.discounts;
  const netPosition = receiptGrandTotal - paymentGrandTotal;

  const handleOpenAddModal = () => {
    setEditingRecordId(null);
    setForm(getInitialFormState("receipt"));
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setEditingRecordId(record.id);
    setForm({
      date: record.date,
      type: record.type,
      particulars: record.particulars || "",
      folio: record.folio || "",
      amount: "",
      accountAffected: record.accountAffected || ACCOUNT_OPTIONS.find((account) => Number(record[account] || 0) > 0) || "cash",
      cash: String(record.cash || ""),
      bank: String(record.bank || ""),
      mpesa: String(record.mpesa || ""),
      discounts: String(record.discounts || ""),
      file: null,
    });
    setShowModal(true);
  };

  const expectedDeleteKeyword = deletingRecord ? `${DELETE_CONFIRMATION_PREFIX}/${deletingRecord.folio || "NO-FOLIO"}` : "";

  const handleDeleteRequest = (record) => {
    setDeletingRecord(record);
    setDeleteKeyword("");
    setDeleteError("");
  };

  const handleCloseDeleteModal = () => {
    if (isDeleting) return;
    setDeletingRecord(null);
    setDeleteKeyword("");
    setDeleteError("");
  };

  const handleConfirmDelete = async (event) => {
    event.preventDefault();
    if (!deletingRecord) return;

    if (deleteKeyword.trim() !== expectedDeleteKeyword) {
      setDeleteError("Keyword mismatch. Deletion cancelled.");
      return;
    }

    if (!firestore) {
      setDeleteError("Audit archive unavailable. Configure Firebase and try again.");
      return;
    }

    setDeleteError("");
    setIsDeleting(true);

    try {
      await addDoc(collection(firestore, DELETED_RECORDS_COLLECTION), {
        ...deletingRecord,
        deletedAt: Date.now(),
        deletedByEmail: currentUserEmail,
        deleteKeyword: expectedDeleteKeyword,
        recordScope,
      });
      revokeIfBlobUrl(deletingRecord.supportingFileUrl);
      setRecords((prev) => prev.filter((record) => record.id !== deletingRecord.id));
      setDeletingRecord(null);
      setDeleteKeyword("");
    } catch (error) {
      setDeleteError("Failed to archive deleted record. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveRecord = (event) => {
    event.preventDefault();

    const baseAmounts = {
      cash: Number(form.cash || 0),
      bank: Number(form.bank || 0),
      mpesa: Number(form.mpesa || 0),
      discounts: Number(form.discounts || 0),
    };

    const quickAmount = Number(form.amount || 0);
    if (
      quickAmount > 0 &&
      ACCOUNT_OPTIONS.includes(form.accountAffected) &&
      Number(baseAmounts[form.accountAffected] || 0) === 0
    ) {
      baseAmounts[form.accountAffected] = quickAmount;
    }

    const normalizedRecord = {
      date: form.date,
      type: form.type,
      particulars: form.particulars.trim(),
      folio: form.folio.trim(),
      accountAffected: form.accountAffected,
      ...baseAmounts,
    };

    setRecords((prev) => {
      if (editingRecordId) {
        return prev.map((record) => {
          if (record.id !== editingRecordId) return record;
          const nextFileName = form.file ? form.file.name : record.supportingFileName;
          const nextFileUrl = form.file ? URL.createObjectURL(form.file) : record.supportingFileUrl;
          if (form.file) {
            revokeIfBlobUrl(record.supportingFileUrl);
          }

          return {
            ...record,
            ...normalizedRecord,
            supportingFileName: nextFileName,
            supportingFileUrl: nextFileUrl,
            lastEditedByEmail: currentUserEmail,
            lastEditedAt: Date.now(),
          };
        });
      }

      const newId = `cb-${Date.now()}`;
      return [
        {
          id: newId,
          ...normalizedRecord,
          createdByEmail: currentUserEmail,
          createdAt: Date.now(),
          lastEditedByEmail: null,
          lastEditedAt: null,
          supportingFileName: form.file?.name || "",
          supportingFileUrl: form.file ? URL.createObjectURL(form.file) : "",
        },
        ...prev,
      ];
    });

    setShowModal(false);
    setEditingRecordId(null);
    setForm(getInitialFormState(form.type));
  };

  return (
    <section className="space-y-4">
      <section className="bg-white border border-slate-100 rounded-xl shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Cashbook</h2>
            <p className="text-sm text-slate-600">Track daily receipts and payments with clear audit-ready records.</p>
          </div>
          <button
            type="button"
            className="px-3 py-2 rounded-lg text-sm font-medium border border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
            onClick={handleOpenAddModal}
          >
            Add Record
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
            <label className="text-xs text-slate-500" htmlFor="cashbook-month">Month</label>
            <select
              id="cashbook-month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(Number(event.target.value))}
              className="text-sm text-slate-700 bg-transparent outline-none"
            >
              {MONTH_OPTIONS.map((month) => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
            <label className="text-xs text-slate-500" htmlFor="cashbook-year">Year</label>
            <select
              id="cashbook-year"
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              className="text-sm text-slate-700 bg-transparent outline-none"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
            <span className="text-xs text-slate-500">From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className="text-sm text-slate-700 outline-none"
            />
            <span className="text-xs text-slate-500">To</span>
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className="text-sm text-slate-700 outline-none"
            />
          </div>
        </div>

        <div className="text-xs text-slate-600">
          Net position: <span className={netPosition >= 0 ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{netPosition >= 0 ? "Positive" : "Negative"}</span> ({formatMoney(netPosition)})
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <RecordsTable
          title="Receipts"
          rows={receipts}
          page={receiptPage}
          onPageChange={setReceiptPage}
          totals={receiptTotals}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
        />
        <RecordsTable
          title="Payments"
          rows={payments}
          page={paymentPage}
          onPageChange={setPaymentPage}
          totals={paymentTotals}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
        />
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRecordId(null);
        }}
        title={editingRecordId ? "Edit Cashbook Record" : "Add Cashbook Record"}
      >
        <form className="space-y-3" onSubmit={handleSaveRecord}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(event) => setForm((prev) => ({ ...prev, date: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Type</label>
              <select
                value={form.type}
                onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="receipt">Receipt</option>
                <option value="payment">Payment</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600">Particulars</label>
            <input
              value={form.particulars}
              onChange={(event) => setForm((prev) => ({ ...prev, particulars: event.target.value }))}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Describe the transaction"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Folio</label>
              <input
                value={form.folio}
                onChange={(event) => setForm((prev) => ({ ...prev, folio: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Reference"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Account Affected</label>
              <select
                value={form.accountAffected}
                onChange={(event) => setForm((prev) => ({ ...prev, accountAffected: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                {ACCOUNT_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600">Quick Amount (uses selected account)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="0.00"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-600">Cash</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.cash}
                onChange={(event) => setForm((prev) => ({ ...prev, cash: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Bank</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.bank}
                onChange={(event) => setForm((prev) => ({ ...prev, bank: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Mpesa</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.mpesa}
                onChange={(event) => setForm((prev) => ({ ...prev, mpesa: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600">Discounts</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.discounts}
                onChange={(event) => setForm((prev) => ({ ...prev, discounts: event.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600">Supporting File</label>
            <input
              type="file"
              onChange={(event) => setForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Creator email is captured as: <span className="font-medium">{currentUserEmail}</span>
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowModal(false)} className="px-3 py-2 border rounded text-sm">Cancel</button>
            <button type="submit" className="px-3 py-2 rounded text-sm bg-slate-900 text-white">{editingRecordId ? "Save Changes" : "Save Record"}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(deletingRecord)} onClose={handleCloseDeleteModal} title="Confirm Record Deletion">
        <form className="space-y-3" onSubmit={handleConfirmDelete}>
          <p className="text-sm text-slate-600">
            Deleting records is restricted for audit reasons.{" "}Enter keyword <span className="font-semibold">{expectedDeleteKeyword}</span> to continue.
          </p>
          <input
            value={deleteKeyword}
            onChange={(event) => setDeleteKeyword(event.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder={expectedDeleteKeyword}
            required
          />
          {deleteError && <p className="text-xs text-rose-700">{deleteError}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={handleCloseDeleteModal} className="px-3 py-2 border rounded text-sm" disabled={isDeleting}>Cancel</button>
            <button type="submit" className="px-3 py-2 rounded text-sm bg-rose-700 text-white disabled:opacity-50" disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete Record"}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export default AccountingCashbookTab;
